import { cn } from '@mdvip/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * Alert: a contained, in-page message for states the member should notice and act on. It stays
 * until dismissed or resolved: use the Toaster for transient confirmations instead.
 *
 * Layout is a grid with an optional leading icon column. Place an `Icon` (or any element that is
 * not AlertTitle/AlertDescription) as the first child to light that column up.
 *
 * Icon selectors cover both `<i>` and `<svg>`: our `Icon` renders a Font Awesome glyph as an `<i>`,
 * while the design system's own source assumes an `<svg>`.
 *
 * There is deliberately no base `[&>i]:text-current`. Every variant colors the `<i>` itself, and a
 * base rule ties with the variant's on specificity, so source order decides. Tailwind emits
 * arbitrary values before named ones, which would let `text-current` beat the warning variant's
 * `text-[var(--melon-700)]` and flatten that icon to the body text color.
 */
const alertVariants = cva(
  'relative grid w-full items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm [&>i]:translate-y-0.5 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'border-border bg-card text-card-foreground [&>i]:text-foreground [&>svg]:text-foreground',
        info: 'border-info/30 bg-info/10 text-foreground [&>i]:text-info [&>svg]:text-info',
        // The warning tone is drawn from the melon ramp rather than `--warning`: our `--warning` is
        // the yellow-500 pair, which gives neither a soft enough surface nor a legible icon at 10%.
        warning:
          'border-[var(--melon-400)] bg-[var(--melon-100)] text-foreground [&>i]:text-[var(--melon-700)] [&>svg]:text-[var(--melon-700)]',
        // The icon takes `text-success`/`text-destructive`, not the `-foreground` pair the design
        // system source uses: ours resolve to near-white `neutral-50`, which disappears on a 10%
        // tint. Same deviation Badge already carries for its tinted variants.
        success: 'border-success/30 bg-success/10 text-foreground [&>i]:text-success [&>svg]:text-success',
        destructive:
          'border-destructive/30 bg-destructive/10 text-foreground [&>i]:text-destructive [&>svg]:text-destructive',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

function Alert({
  className,
  variant,
  children,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  const hasIcon = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type !== AlertTitle && child.type !== AlertDescription,
  );

  return (
    <div
      role="alert"
      data-slot="alert"
      className={cn(
        alertVariants({ variant }),
        hasIcon ? 'grid-cols-[calc(var(--spacing)*4)_1fr] gap-x-3' : 'grid-cols-[0_1fr]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight text-foreground', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle, alertVariants };
